import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, type SessionUser, type SystemStatus } from '@voice/shared';
import { Button, EmptyState, Mono, PageHeader, RowActions, StatusIndicator, Table, Tag } from '@voice/ui';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { LiveCall, LiveResponse } from '../lib/types';
import styles from './Dashboard.module.css';
import page from './Pages.module.css';

export function Dashboard({ user }: { user: SessionUser }) {
  const canSeeSystem = hasPermission(user.permissions, 'system.view');

  return (
    <div className={styles.page}>
      <PageHeader title="Übersicht" />
      {canSeeSystem ? (
        <>
          <SystemStatusTable />
          <LiveSection />
        </>
      ) : (
        <p className={styles.note}>Hallo {user.name}. Für dein Konto sind noch keine Bereiche freigeschaltet.</p>
      )}
    </div>
  );
}

function SystemStatusTable() {
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api<SystemStatus>('/system/status'),
    refetchInterval: 10_000,
  });

  return (
    <section aria-labelledby="system-heading">
      <h2 id="system-heading" className={styles.heading}>
        System
      </h2>
      {status.isPending && <p className={styles.note}>Lädt …</p>}
      {status.isError && <p className={styles.note}>Status konnte nicht geladen werden.</p>}
      {status.data && (
        <table className={styles.table}>
          <thead className="visually-hidden">
            <tr>
              <th>Bereich</th>
              <th>Zustand</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {status.data.components.map((c) => (
              <tr key={c.id}>
                <th scope="row" className={styles.cellName}>
                  {c.label}
                </th>
                <td>
                  <StatusIndicator state={c.state === 'ok' ? 'ok' : 'busy'} label={c.state === 'ok' ? 'In Ordnung' : 'Gestört'} />
                </td>
                <td className={styles.detail}>{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const STATE_LABEL: Record<string, string> = {
  setup: 'Wird aufgebaut',
  ringing: 'Klingelt',
  ivr: 'Ansage / Menü',
  queued: 'In der Warteschlange',
  answered: 'Im Gespräch',
  held: 'Gehalten',
  parked: 'Geparkt',
};

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function duration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const party = (p: { number: string; name?: string | undefined } | undefined) => (p ? (p.name && p.name !== p.number ? `${p.name} (${p.number})` : p.number) : '–');

function LiveSection() {
  const qc = useQueryClient();
  const live = useQuery({ queryKey: ['live'], queryFn: () => api<LiveResponse>('/live'), refetchInterval: 3000 });
  const now = useNow(1000);
  const unban = useMutation({ mutationFn: (ip: string) => api(`/bans/${encodeURIComponent(ip)}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['live'] }) });

  if (!live.data) return null;
  const { calls, registrations, banned } = live.data;

  return (
    <>
      <section className={page.section} aria-labelledby="calls-heading">
        <h2 id="calls-heading" className={page.heading}>
          Laufende Gespräche
        </h2>
        {calls.length === 0 ? (
          <EmptyState>Im Moment telefoniert niemand.</EmptyState>
        ) : (
          <Table aria-label="Laufende Gespräche">
            <thead>
              <tr>
                <th>Von</th>
                <th>Gewählt</th>
                <th>Verbunden mit</th>
                <th>Zustand</th>
                <th>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c: LiveCall) => (
                <tr key={c.id}>
                  <th scope="row">{party(c.caller)}</th>
                  <td>{c.dialed}</td>
                  <td>{party(c.connectedTo)}</td>
                  <td>
                    <Tag tone={c.state === 'answered' ? 'ok' : c.state === 'ringing' ? 'warn' : 'neutral'}>{STATE_LABEL[c.state] ?? c.state}</Tag>
                  </td>
                  <td>
                    <Mono>{duration(now - (c.answeredAt ?? c.startedAt))}</Mono>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className={page.section} aria-labelledby="regs-heading">
        <h2 id="regs-heading" className={page.heading}>
          Angemeldete Geräte
        </h2>
        {registrations.length === 0 ? (
          <EmptyState>Kein Gerät ist angemeldet.</EmptyState>
        ) : (
          <Table aria-label="Angemeldete Geräte">
            <thead>
              <tr>
                <th>Nebenstelle</th>
                <th>Verbindung</th>
                <th>Programm</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((r, i) => (
                <tr key={`${r.extension}-${i}`}>
                  <th scope="row">{r.extension}</th>
                  <td>
                    <Mono>{r.via}</Mono>
                  </td>
                  <td className={page.muted}>{r.userAgent ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {banned.length > 0 && (
        <section className={page.section} aria-labelledby="bans-heading">
          <h2 id="bans-heading" className={page.heading}>
            Gesperrte Adressen
          </h2>
          <Table aria-label="Gesperrte Adressen">
            <tbody>
              {banned.map((b) => (
                <tr key={b.ip}>
                  <th scope="row">
                    <Mono>{b.ip}</Mono>
                  </th>
                  <td className={page.muted}>bis {new Date(b.until).toLocaleTimeString('de-DE')} (zu viele falsche Anmeldungen)</td>
                  <td>
                    <RowActions>
                      <Button variant="secondary" onClick={() => unban.mutate(b.ip)} disabled={unban.isPending}>
                        Freigeben
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}
    </>
  );
}
